// The catnip trip: the race's highlight. Ties the beat sequencer to the cats, the environment's
// lane and sky shaders, the trip visuals, the screen effect, the camera, the live inset and the
// landing call-out.
//
//   const trip = new CatnipTrip({ scene, cats, environment, container, camera });
//   post.addEffect(trip.effect);
//   loop: trip.update(snapshot, events, peek, dt, timeS)   // after the camera rig, before cats.update
//         post.render(); trip.renderInset(renderer)
//
// The trip owns a cat through CatActor.override from the catch until it has landed and staggered,
// then hands it back to normal running at the spot the signal reports. While the camera director
// does not exist, the trip frames the main camera itself (`takeCamera`); a director should set
// `takeCamera = false` and use `cameraHint()` for its trip shots instead.
import { type PerspectiveCamera, Quaternion, type Scene, Vector3, type WebGLRenderer } from 'three';
import type { CatId, RaceEvent, RaceSnapshot } from '../../race/types';
import { CAT_DISPLAY_SCALE, catById } from '../../data/cats';
import { laneCenterZ, posToX } from '../../data/track';
import type { CatField } from '../cats/catField';
import type { Environment } from '../environment';
import { TripEffect } from '../post/tripEffect';
import { LiveInset } from './inset';
import { type TripFrame, TripSequencer } from './sequencer';
import { LandingPuff, LaneCurtain, TripAura } from './visuals';
import { renderStep } from '../../app/renderStep';

export { TripSequencer } from './sequencer';
export type { TripBeat, TripFrame, TripPose } from './sequencer';

export interface CatnipTripDeps {
  scene: Scene;
  cats: CatField | null;
  environment: Environment | null;
  /** Element the canvas lives in; the inset frame and call-out are placed over it. */
  container: HTMLElement;
  camera: PerspectiveCamera;
}

/** Where a camera should be for the featured trip right now (for a camera director). */
export interface TripCameraHint {
  cat: CatId;
  beat: TripFrame['beat'];
  position: Vector3;
  target: Vector3;
  fov: number;
}

const _q0 = new Quaternion();
const _q1 = new Quaternion();
const _v = new Vector3();
const _c = new Vector3();
const _d = new Vector3();
const _dir = new Vector3();
const _off = new Vector3();
const _aim = new Vector3();

export class CatnipTrip {
  readonly effect = new TripEffect();
  readonly sequencer = new TripSequencer();
  readonly inset: LiveInset;
  /** Frame the main camera on the trip while no director does it. */
  takeCamera = true;
  /** Show the landing call-out; off while the title screen is up. */
  showCallouts = true;
  private lastT = Number.NaN;
  private aura = new TripAura();
  private curtain = new LaneCurtain();
  private puff = new LandingPuff();
  private frames: TripFrame[] = [];
  private featured: TripFrame | null = null;
  private owned = new Set<CatId>();
  private camWeight = 0;
  private shotPos = new Vector3();
  private shotTarget = new Vector3();
  private shotOff = new Vector3();
  private shotAim = new Vector3();
  private shotFov = 40;
  private shotBeat: TripFrame['beat'] | null = null;
  private baseFov: number;
  private skyFade = 0;
  private lastLeader: CatId | null = null;

  constructor(private d: CatnipTripDeps) {
    d.scene.add(this.aura.group, this.curtain.mesh, this.puff.points);
    this.inset = new LiveInset(d.container);
    this.baseFov = d.camera.fov;
  }

  /** The trips in progress (one per tripping cat), for HUD or director use. */
  get active(): readonly TripFrame[] { return this.frames; }

  /** Call once per frame after the camera rig has placed the camera and before the cats update. */
  update(snap: RaceSnapshot, events: readonly RaceEvent[], peek: readonly RaceEvent[], dt: number, timeS: number): void {
    const frames = this.sequencer.update(snap.t, snap.cats, events, peek);
    this.frames = frames;

    // ---- the cats: take over, pose, hand back
    const posed = new Set<CatId>();
    for (const f of frames) {
      const actor = this.d.cats?.actor(f.cat);
      if (!actor) continue;
      if (f.pose) {
        actor.override = {
          position: new Vector3(f.pose.x, f.pose.y, f.pose.z), heading: f.pose.heading, roll: f.pose.roll,
          pitch: f.pose.pitch, spin: f.pose.spin, mode: f.pose.mode, tripEyes: f.pose.tripEyes,
          speed: f.pose.mode === 'run' ? 0 : undefined,
        };
        posed.add(f.cat);
        this.owned.add(f.cat);
      }
      if (f.touchdown) this.onTouchdown(f, snap);
      if (this.showCallouts && f.beat === 'catch' && f.again && f.elapsed < 50) {
        this.inset.announce('Again?!', `${catById(f.cat).shortName} is back on the catnip`, 2.4);
      }
    }
    for (const id of [...this.owned]) {
      if (posed.has(id)) continue;
      const actor = this.d.cats?.actor(id);
      if (actor) actor.override = null;
      this.owned.delete(id);
    }

    // ---- the featured trip: the most recent catch that is still airborne or just landed
    const airborne = frames.filter((f) => f.pose);
    this.featured = airborne.length ? airborne.reduce((a, b) => (a.elapsed < b.elapsed ? a : b)) : null;
    const draining = frames.filter((f) => !f.pose);

    // ---- lanes: each tripping lane swirls, then drains
    const lanes = this.d.environment?.laneUniforms;
    if (lanes) {
      for (let i = 0; i < lanes.length; i++) {
        const f = frames.find((x) => x.lane === i);
        lanes[i]!.tripIntensity.value = f ? f.laneGlow : 0;
      }
    }

    // ---- camera: cut to the trip, follow its beats, then hand the camera back
    const f = this.featured;
    const cam = this.d.camera;
    const wantCam = this.takeCamera && !!f;
    // Ease on race render time, not the loop's capped dt, so the camera keeps up at any frame rate.
    const camDt = renderStep(this.lastT, snap.t, dt);
    this.lastT = snap.t;
    this.camWeight += ((wantCam ? 1 : 0) - this.camWeight) * (1 - Math.exp(-camDt / (wantCam ? 0.18 : 0.9)));
    if (f?.pose) this.computeShot(f, timeS, camDt);
    if (this.camWeight > 0.002 && this.takeCamera) {
      _q0.copy(cam.quaternion);
      const basePos = _v.copy(cam.position);
      cam.position.lerpVectors(basePos, this.shotPos, this.camWeight);
      cam.lookAt(this.shotTarget);
      _q1.copy(cam.quaternion);
      cam.quaternion.slerpQuaternions(_q0, _q1, this.camWeight);
      cam.fov = this.baseFov + (this.shotFov - this.baseFov) * this.camWeight;
      cam.updateProjectionMatrix();
    } else if (cam.fov !== this.baseFov) {
      cam.fov = this.baseFov;
      cam.updateProjectionMatrix();
    }

    // ---- sky, screen effect, aura and curtain follow the featured trip, and only when it is on screen
    const onScreen = this.takeCamera ? this.camWeight : 0;
    const skyTarget = f ? f.sky * onScreen : 0;
    this.skyFade += (skyTarget - this.skyFade) * (1 - Math.exp(-dt / 0.3));
    const sky = this.d.environment?.sky.uniforms.uTripIntensity;
    if (sky) sky.value = this.skyFade;
    this.effect.strength = f ? f.screen * onScreen : 0;
    if (f?.pose) {
      const centre = _v.set(f.pose.x, f.pose.y + 0.22, f.pose.z);
      const depth = _d.copy(centre).sub(cam.position).dot(cam.getWorldDirection(_dir));
      this.aura.update(f.beat === 'catch' ? 0 : Math.min(1, f.sky * 1.15), centre, timeS, depth);
      // keep the warp off the cat: its body (about 0.45 m round) as a share of the screen height
      this.effect.keepRadius = 0.45 * CAT_DISPLAY_SCALE / (Math.max(0.5, depth) * Math.tan((cam.fov * Math.PI) / 360)) * 0.5;
      const p = centre.clone().project(cam);
      this.effect.center.set(Math.min(1, Math.max(0, p.x * 0.5 + 0.5)), Math.min(1, Math.max(0, p.y * 0.5 + 0.5)));
    } else {
      this.aura.update(0, _v.set(0, 0, 0), timeS);
    }
    const curtainSrc = f ?? draining[0] ?? null;
    if (curtainSrc) {
      const x = curtainSrc.pose ? curtainSrc.pose.x : this.d.cats ? this.d.cats.actor(curtainSrc.cat).bodyCentre(_v).x : 0;
      // softer at the catch and the landing, where the cat is close and on the ground inside it
      const strength = curtainSrc.laneGlow * (curtainSrc.beat === 'catch' ? 0.4 : curtainSrc.beat === 'landed' ? 0.45 : curtainSrc.beat === 'drain' ? 0.6 : 1);
      this.curtain.update(strength, x, laneCenterZ(curtainSrc.lane), timeS);
    } else this.curtain.update(0, 0, 0, timeS);

    // ---- landing puff and the inset
    const h = (this.d.container.clientHeight || innerHeight) * Math.min(devicePixelRatio || 1, 1.5);
    this.puff.update(dt, h);
    const leader = snap.cats.reduce<{ id: CatId; place: number } | null>((a, c) => (!a || c.place < a.place ? { id: c.id, place: c.place } : a), null)?.id ?? null;
    const leadChanged = leader !== null && this.lastLeader !== null && leader !== this.lastLeader;
    this.lastLeader = leader;
    const showInset = this.showCallouts && !!f && (f.beat === 'lift' || f.beat === 'trip' || f.beat === 'descent') && onScreen > 0.5;
    this.inset.update(snap, showInset, leadChanged, f?.cat ?? null, dt);
  }

  /** The featured trip in plain terms, for the test hook. */
  featuredInfo(): { cat: CatId; beat: TripFrame['beat']; elapsedMs: number; beatT: number } | null {
    const f = this.featured;
    return f ? { cat: f.cat, beat: f.beat, elapsedMs: f.elapsed, beatT: f.beatT } : null;
  }

  /** Where a director should put its camera for the featured trip, or null when there is none. */
  cameraHint(): TripCameraHint | null {
    const f = this.featured;
    if (!f) return null;
    return { cat: f.cat, beat: f.beat, position: this.shotPos.clone(), target: this.shotTarget.clone(), fov: this.shotFov };
  }

  /** Draw the live-race inset over the finished frame. Call right after post.render(). */
  renderInset(renderer: WebGLRenderer): void {
    const sky = this.d.environment?.sky.uniforms.uTripIntensity;
    let saved = 0;
    let hideCats = (): void => {};
    this.inset.render(renderer, this.d.scene,
      () => {
        saved = sky?.value ?? 0; if (sky) sky.value = 0; this.aura.group.visible = false;
        // the cats are culled for the main camera; show the ones the live picture is on
        if (this.d.cats) hideCats = this.d.cats.revealFor(this.inset.camera);
      },
      () => { if (sky) sky.value = saved; this.aura.group.visible = this.featured !== null; hideCats(); });
  }

  /** Compiles the trip's shaders and the inset's tone-mapped variants during loading. */
  async compile(renderer: WebGLRenderer): Promise<void> {
    this.aura.group.visible = this.curtain.mesh.visible = this.puff.points.visible = true;
    try {
      await renderer.compileAsync(this.d.scene, this.d.camera);
      await this.inset.compile(renderer, this.d.scene);
    } finally {
      this.aura.group.visible = this.curtain.mesh.visible = this.puff.points.visible = false;
    }
  }

  private onTouchdown(f: TripFrame, snap: RaceSnapshot): void {
    const L = f.landing;
    if (!L) return;
    this.puff.burst(f.pose ? f.pose.x : 0, f.pose ? f.pose.z : 0);
    const c = snap.cats.find((x) => x.id === f.cat);
    if (this.showCallouts) this.inset.announceLanding(f.cat, L.delta, L.to, c ? c.place : null);
  }

  /**
   * The trip's own shots, beat by beat. Every shot is an offset from the cat's body, so the camera
   * travels with the cat (the descent can cross a hundred metres in a second and a half) and only
   * the framing eases between beats. Distances keep the cat about a third of the frame high.
   */
  private computeShot(f: TripFrame, timeS: number, dt: number): void {
    const p = f.pose!;
    const c = _c.set(p.x, p.y + 0.2, p.z);
    const off = _off, aim = _aim; // camera and look-at, relative to the cat's body
    let fov: number;
    switch (f.beat) {
      case 'catch': // front quarter, close on the face as it bites the leaf
        off.set(1.75, 0.22, 1.5); aim.set(0.1, 0, 0); fov = 34; break;
      case 'lift': // rises with the cat, a touch below it, so it floats up out of its lane whole
        off.set(2.2, -0.15 - 0.25 * f.beatT, 2.35); aim.set(0, 0.05, 0); fov = 38; break;
      case 'trip': { // slow orbit round the cat at its own height, the folded stadium behind it
        const a = timeS * 0.3 + 0.75;
        off.set(Math.cos(a) * 2.8, 0.15 + Math.sin(timeS * 0.45) * 0.3, Math.abs(Math.sin(a)) * 2.8 + 0.4);
        aim.set(0, 0, 0); fov = 40; break;
      }
      case 'descent': { // chase from behind and above as it falls, the landing spot ahead in view
        const dir = f.landing && posToX(f.landing.to) < posToX(f.landing.from) ? -1 : 1;
        off.set(-dir * 3.0, 0.95, 2.5); aim.set(dir * 1.1, -0.35, 0); fov = 40; break;
      }
      default: // landed: front quarter on the cat at its new spot, wide enough for the lane and puff
        off.set(2.1, 0.4, 2.3); aim.set(0.3, -0.05, 0); fov = 36;
    }
    // cut on the catch; ease the framing between the other beats
    const cut = this.shotBeat !== f.beat && f.beat === 'catch';
    const k = cut || this.camWeight < 0.05 ? 1 : 1 - Math.exp(-dt / 0.45);
    this.shotOff.lerp(off, k);
    this.shotAim.lerp(aim, k);
    this.shotFov += (fov - this.shotFov) * k;
    this.shotBeat = f.beat;
    this.shotPos.copy(c).add(this.shotOff);
    this.shotPos.y = Math.max(0.28, this.shotPos.y); // never under the grass
    this.shotTarget.copy(c).add(this.shotAim);
  }

  dispose(): void {
    this.effect.dispose();
    this.inset.dispose();
  }
}
